/* eslint-disable max-lines */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ContentHeadingData } from 'common/models/content-heading';
import { ToastService } from 'common/services/toast.service';
import { AccountAdminCountriesEntity, Company } from 'private/app/models/account-admin-search';
import { UserDetailsEntity, ExportUserDetailsEntity, UserDetails, CreateUserDetails } from 'private/app/models/user.model';
import { AccountAdminCompanyUtilsService } from 'private/app/services/account-admin/account-admin-company-utils.service';
import { AccountAdminUtilsService } from 'private/app/services/account-admin/account-admin-utils.service';
import { AccountAdminService } from 'private/app/services/account-admin/account-admin.service';
import { UserAdminService } from 'private/app/services/account-admin/user/user-admin.service';
import { UserApiService } from 'private/app/services/account-admin/user/user-api.service';
import { BehaviorSubject } from 'rxjs';
import { Option } from '../../../../../../common/components/select/select.component';
import { CompanyResultDataResponse } from 'private/app/models/account-admin-search-results';
import { AccountAdminApiService } from 'private/app/services/account-admin/account-admin-api.service';
import { CommonUtilsService } from 'private/app/services/common-utils.service';
import { AccountAdminExportService } from '../../../../services/account-admin/account-admin-export.service';
import { AccountAdminExtendedService } from 'private/app/services/account-admin/account-admin-extended.service';
import { OktaService } from 'common/services/okta/okta.service';
import { IdToken } from 'common/models';
import { BrandFamily } from 'common/models/brand.model';
import { AccountStatusService } from 'common/services/account-status.service';
import { AccountStatusResponse } from 'common/models/account-status';
import { AppConstants } from 'common/app-constants';
import { CountryForMasking } from 'private/app/services/account-admin/account-admin-company-create.service';
import { PlaceHolderText } from 'private/app/models/account-admin-company.model';
import { IdmUserRoles } from 'private/app/enums/idm-user-roles';
import { CompanyCategoryType } from 'private/app/views/account-admin-company-details/company-query/company-query-configuration';

type activeScreensType = 'ReadOnlyUserInfo' | 'EditInfo' | 'EditSecurity' | 'EditRelations' | 'CloneUser' | 'LayerView';
type SearchCompany = {
    userCreateStep: string,
    company: CompanyResultDataResponse
}

const hybrisEligibleFeatureNamesList = ['ordercorrespondent', 'fastpartordercorrespondent', 'b2bcustomergroup', 'inquiryuser', 'internalcustomersupport', 'respricinganalyst', 'copsgroup', 'cdordercorrespondent'];
const relationshipEligibleCategories: string[] = [
    CompanyCategoryType.INTERNAL,
    CompanyCategoryType.DOMESTIC_DISTRIBUTOR,
    CompanyCategoryType.INTERNATIONAL_DISTRIBUTOR,
    CompanyCategoryType.MARINE_DISTRIBUTOR
];

@Component({
    selector: 'hvac-user-details',
    templateUrl: './user-details.component.html',
    styleUrls: ['./user-details.component.scss']
})
export class UserDetailsComponent implements OnInit, OnDestroy {
    placeHolderByCountry$: BehaviorSubject<PlaceHolderText> = new BehaviorSubject<PlaceHolderText>({
        placeHolderPhone: '',
        placeHolderOffice: '',
        placeHolderMobile: '',
        placeHolderFax: '',
        placeHolderTollFree: '',
        placeHolderZipcode: { label: '' }
    });

    public company: CompanyResultDataResponse;
    public userDetails: UserDetails;
    public createUserOnStepNumber: string;
    public activeScreenName: string;
    public breadCrumbData: ContentHeadingData = this.userAdminService.breadCrumbData;
    public user: UserDetailsEntity;
    public editUserInfo: boolean;
    public apiRequestActive = false;
    public userCountry: string;
    public userState: string;
    public activeUserScreen: activeScreensType = 'ReadOnlyUserInfo';
    public countryOptionData$: BehaviorSubject<Option[]> = new BehaviorSubject<Option[]>([]);
    public stateOptionData$: BehaviorSubject<Option[]> = new BehaviorSubject<Option[]>([]);
    public editUserInfo$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public userCompanyTypeName = '';
    public userCompanyTypeCode = '';
    public userCompanyBrandFamily = '';
    public userID: string;
    public isLoading = false;
    public isInternalSuperAdmin?: boolean;
    public isUserAdmin?: boolean;
    public isDefaultInternal?: boolean;
    public isSAPAdmin?: boolean;
    public deactivateUserModal: boolean = false;
    public activateUserModal: boolean = false;
    public isICPUser?: boolean;
    public initialContactOnly: boolean;
    public isHybrisEligible?: boolean = false;
    public loggedInUserBrandFamily: string;
    public maskPhoneNumber: boolean = false;
    public canManageUserRelationship = false;
    private _userCompanyEditMode = false;
    private _isCompanyListDataAvailable = false;
    private userExportDetails: ExportUserDetailsEntity = {} as ExportUserDetailsEntity;
    private pendingUserCreationSuccessMessage?: string;

    constructor(
        private translate: TranslateService,
        public accountAdminService: AccountAdminService,
        private userApiService: UserApiService,
        private route: ActivatedRoute,
        private router: Router,
        private readonly toastService: ToastService,
        private readonly statusService: AccountStatusService,
        public userAdminService: UserAdminService,
        private accountAdminCompanyUtilsService: AccountAdminCompanyUtilsService,
        private accountAdminUtilsService: AccountAdminUtilsService,
        private accountAdminApiService: AccountAdminApiService,
        private commonUtils: CommonUtilsService,
        private ExportService: AccountAdminExportService,
        private oktaService: OktaService,
        private accountAdminExtendedService: AccountAdminExtendedService
    ) {
        this.onCompanySelect = this.onCompanySelect.bind(this);
    }

    get userCompanyEditMode(): boolean {
        return this._userCompanyEditMode;
    }

    get isCompanyListDataAvailable(): boolean {
        return this._isCompanyListDataAvailable;
    }

    @HostListener('document:click', ['$event'])
    onClick(event: MouseEvent) {
        const clickedInsideMenu = (event.target as HTMLElement).closest('.hvac-dropdown-menu') !== null;
        const clickedOnButton = (event.target as HTMLElement).closest('.hvac-more-options') !== null;
        if (!clickedInsideMenu && !clickedOnButton) {
            this.userAdminService.menuActive = false;
        }
    }

    ngOnInit(): void {
        const navigationState = history.state as { userCreationSuccessMessage?: string };
        if (navigationState?.userCreationSuccessMessage) {
            this.pendingUserCreationSuccessMessage = navigationState.userCreationSuccessMessage;
        }

        // SUBSCRIPTIONS
        this.oktaService.idToken$.subscribe((res: IdToken) => {
            this.isDefaultInternal = res?.claims?.idm_user_roles?.includes(IdmUserRoles.DEFAULT_INTERNAL);
            this.isUserAdmin = res?.claims?.idm_user_roles?.includes(IdmUserRoles.USERADMIN);
            this.isInternalSuperAdmin = res?.claims?.idm_user_roles?.includes(IdmUserRoles.INTERNAL_SUPER_ADMIN);
            this.isSAPAdmin = res?.claims?.idm_user_roles?.includes(IdmUserRoles.SAP_ADMIN);
        });
        this.statusService.getUserStatus().subscribe((result: AccountStatusResponse) => {
            this.loggedInUserBrandFamily = result.brandFamily;
        });
        this.accountAdminUtilsService.setAdminBreadCrumbData(this.breadCrumbData);
        this.commonUtils.getCountriesEntity().subscribe((countryList) => {
            const optionList: Option[] = [];
            optionList.push({
                name: this.translate.instant('ACCOUNT_ADMIN.SELECT_COUNTRY'),
                value: ''
            });
            countryList.forEach((country: AccountAdminCountriesEntity) => {
                optionList.push({
                    name: country.name,
                    value: country.code
                });
            });
            this.countryOptionData$.next(this.accountAdminCompanyUtilsService.filterCountryArraywithMostUsed(optionList));
            if (this.user) {
                const code = this.user.userCountry || '';
                this.userCountry = code ? (this.countryOptionData$.value.find((country) => country.value === code)?.name || '') : '';
                if (code) {
                    this.getStateListByCountry(code);
                }
            }
        });
        this.userAdminService.menuActive = false;
        this.editUserInfo$.subscribe((isUserInEditInfoPage) => {
            this.userAdminService.smoothScrollToTop();
            if (isUserInEditInfoPage) {
                this.activeUserScreen = 'EditInfo';
                this.updateUIWithUser();

                return;
            }
            this.activeUserScreen = 'ReadOnlyUserInfo';

            this.isLoading = true;
            this.getUserDetailById(this.route.snapshot.params.id);
            this.userAdminService.smoothScrollToTop();
        });
        this.accountAdminCompanyUtilsService.placeHolderData$.subscribe((placeHolderData) => {
            this.placeHolderByCountry$.next(placeHolderData);
        });
        this.userAdminService.userEditForm.controls.userCountry.valueChanges.subscribe((selectedCountry: string) => {
            if (Array.isArray(selectedCountry)) {
                this.maskPhoneNumber = Boolean(selectedCountry[0].value === CountryForMasking.US || selectedCountry[0].value === CountryForMasking.CA);
                this.userAdminService.userEditForm.controls.userState.setValue('', { emitEvent: true });
                this.userAdminService.userEditForm.controls.userState.markAsDirty();
                this.accountAdminCompanyUtilsService.formInputPlaceHolder(selectedCountry[0].value);
                this.getStateListByCountry(selectedCountry[0].value);
                this.userAdminService.updatePostalCodeValidator(this.userAdminService.userEditForm, selectedCountry[0].value);
                this.commonUtils.updatePhoneValidators(this.userAdminService.userEditForm, selectedCountry[0].value, ['userPhone', 'userCellPhone', 'userFax']);
            }
            else {
                const countrySelection = this.countryOptionData$.value.find((country) => country.name === selectedCountry);
                if (countrySelection) {
                    this.maskPhoneNumber = countrySelection.value === CountryForMasking.US || countrySelection.value === CountryForMasking.CA;
                    this.getStateListByCountry(countrySelection.value);
                    this.accountAdminCompanyUtilsService.formInputPlaceHolder(countrySelection.value);
                    this.userAdminService.updatePostalCodeValidator(this.userAdminService.userEditForm, countrySelection.value);
                    this.commonUtils.updatePhoneValidators(this.userAdminService.userEditForm, countrySelection.value, ['userPhone', 'userCellPhone', 'userFax']);
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.accountAdminUtilsService.setAdminBreadCrumbData(this.accountAdminUtilsService.breadCrumbData);
    }

    getUserDetailById(id: string) {
        this.isLoading = true;
        this.userApiService.getUserDetailsByIdV1(id).subscribe((data) => {
            this.user = data;
            this.isHybrisEligible = this.checkIfUserIsHybrisEligible();
            this.isICPUser = this.user.brandFamily.toLowerCase() === BrandFamily.ICP;
            const countryCode = this.user.userCountry || '';
            this.userCountry = countryCode ? (this.countryOptionData$.value.find((country) => country.value === countryCode)?.name || '') : '';
            this.getStateListByCountry(countryCode);
            this.initialContactOnly = this.user.contactOnly;
            this.setUserRelationshipEligibility(this.user.companyId);
            this.toastService.removeAll();
            this.showPendingUserCreationSuccessToast();
        }, (error) => {
            this.isLoading = false;
            this.userAdminService.showToastBar('error', error.message, 'getUserDetailByIdError');
        });
    }

    updateUIWithUser() {
        this.userAdminService.userEditForm.setValue({
            firstName: this.user.firstName || '',
            lastName: this.user.lastName || '',
            userID: this.user.userID || '',
            userTitle: this.user.userTitle || '',
            employerName: this.user.employerName || '',

            companyName: this.user.employerName || '',
            companyId: this.user.companyId || '',
            hvacPin: this.user.hvacPin || '',

            userAddress1: this.user.userAddress1 || '',
            userAddress2: this.user.userAddress2 || '',
            userCity: this.user.userCity || '',

            userState: this.stateOptionData$.value.find((state) => state.value === this.user.userState)?.name || '',
            userPostalCode: this.user.userPostalCode || '',
            userCountry: this.userCountry || '',

            userPhone: this.commonUtils.formatPhoneNumber(this.user.userPhone || '', this.user.userCountry),
            userCellPhone: this.commonUtils.formatPhoneNumber(this.user.userCellPhone || '', this.user.userCountry),
            userFax: this.commonUtils.formatPhoneNumber(this.user.userFax || '', this.user.userCountry),

            email: this.user.email || '',
            contactOnly: this.user.contactOnly || '',

            lastLoginDate: this.user.lastLoginDate || '',

            registrationAction: 'Select',
            comments: ''
        }, { emitEvent: true });
    }

    setContactOnly(event: Event) {
        const input = <HTMLInputElement>event.target;
        if (input) {
            this.userAdminService.userEditForm.controls.contactOnly.setValue(input.checked);
            this.user.contactOnly = input.checked;
        }
    }

    resetPasswordFunction(event: Event) {
        const input = <HTMLInputElement>event.target;
        if (input) {
            this.user.resetPassword = input.checked;
        }
    }

    validatePassword(event: Event) {
        const input = <HTMLInputElement>event.target;
        if (input) {
            this.user.isValidationRequired = input.checked;
        }
    }

    submitChanges() {
        this.apiRequestActive = true;
        this.isLoading = true;
        this.userApiService.updateUserProfileDetailsV1(this.userAdminService.createUserPostBody(this.user)).subscribe((response) => {
            this.apiRequestActive = false;
            this.userAdminService.smoothScrollToTop();
            this.userAdminService.showToastBar('success', response.details, 'updateUserProfileDetails');
            this.userAdminService.userEditForm.markAsPristine();
            setTimeout(() => {
                this.editUserInfo$.next(false);

                return;
            }, AppConstants.timeOutNavigation);
        }, (error) => {
            this.isLoading = false;
            this.apiRequestActive = false;
            this.userAdminService.showToastBar('error', error.message, 'updateUserProfileDetailsError');
        });
    }

    onCountryChange(_option: Option[]) {
        this.user.userCountry = this.getSelectedCountryCode();
    }

    onStateChange(_option: Option[]) {
        this.user.userState = this.getSelectedStateCode();
    }

    enableUserRelationshipsEditMode() {
        if (this.canManageUserRelationship) {
            this.activeUserScreen = 'EditRelations';
        }
    }

    isCancelButtonclicked(event: boolean) {
        (event) ? this.activeUserScreen = 'ReadOnlyUserInfo' : '';
        this.userAdminService.smoothScrollToTop();
    }

    onClickEditSecurity(isUserInEditSecurity: boolean) {
        if (isUserInEditSecurity) {
            this.userAdminService.menuActive = false;
            this.activeUserScreen = 'EditSecurity';
        }
        else {
            this.activeUserScreen = 'ReadOnlyUserInfo';
            this.userAdminService.smoothScrollToTop();
        }
    }

    onUserSecurityUpdated() {
        this.getUserDetailById(this.route.snapshot.params.id);
    }

    onPostalCodeInput(event: Event) {
        const input = event.target as HTMLInputElement;
        if (!this.getSelectedCountryCode()) {
            return;
        }

        const formattedValue = this.commonUtils.formatPostalCode(input.value, this.getSelectedCountryCode());
        input.value = formattedValue;
        this.userAdminService.userEditForm.controls.userPostalCode.setValue(formattedValue, { emitEvent: false });
    }

    onClickCloneUser() {
        this.activeUserScreen = 'CloneUser';
        this.createUserOnStepNumber = '';
        this.userAdminService.menuActive = false;
    }

    onClickLayerView() {
        this.activeUserScreen = 'LayerView';
        this.userAdminService.menuActive = false;
    }

    backFromLayerView() {
        this.activeUserScreen = 'ReadOnlyUserInfo';
    }

    onContinueToStep2Click(event: string) {
        this.createUserOnStepNumber = event;
    }

    onClickSyncUserToServices(serviceName: string) {
        this.isLoading = true;
        this.userApiService.syncUserToServicesApi(serviceName, this.user).subscribe((response) => {
            this.isLoading = false;
            if (response.status === 'Success') {
                this.userAdminService.showToastBar('success', `User ${this.user.userID} has been successfully updated in ${serviceName}.`, serviceName);
            }
        }, (error) => {
            this.isLoading = false;
            if (error) {
                this.userAdminService.showToastBar('error', `There was an issue syncing user ${this.user.userID} to ${serviceName}.`, serviceName);
            }
        });
    }

    userCreateDetailsSubmit(event : CreateUserDetails) {
        this.isLoading = true;
        this.apiRequestActive = true;
        this.userApiService.createOrRegisterUserV1(event).subscribe((response) => {
            this.apiRequestActive = false;
            this.userAdminService.smoothScrollToTop();
            this.userAdminService.showToastBar('success', response.details, 'createOrRegisterUser');
            this.activeUserScreen = 'ReadOnlyUserInfo';
            this.createUserOnStepNumber = '';
            this.userAdminService.userEditForm.markAsPristine();
            if (response && response.details) {
                this.userID = response.details.split(' : ').pop()!;
            }
            else {
                this.userID = '';
            }

            setTimeout(() => {
                this.isLoading = false;
                this.router.navigate([`/Admin/Admin-Tools/account-admin.html/user-details/${this.userID}`], { relativeTo: this.route });
                this.getUserDetailById(this.userID);

                return;
            }, AppConstants.timeOutNavigation5Sec);
        }, (error) => {
            this.isLoading = false;
            this.apiRequestActive = false;
            this.userAdminService.smoothScrollToTop();
            this.userAdminService.showToastBar('error', error.error.details, 'createOrRegisterUserError');
        });
    }

    onClickCreateUserChangePage(event : SearchCompany) {
        const { company, userCreateStep } = event;
        this.accountAdminCompanyUtilsService.getAccountAdminUsersByCompanyId(this.user.companyId.toString());
        this.company = company;
        this.userAdminService.smoothScrollToTop();
        if (userCreateStep === 'ReadOnlyUserInfo') {
            this.activeUserScreen = 'ReadOnlyUserInfo';
            this.createUserOnStepNumber = '';
            this.userAdminService.smoothScrollToTop();
        }
        else {
            this.createUserOnStepNumber = 'step4_userRolesFeatures';
        }
    }

    deactivateUser() {
        this.deactivateUserModal = false;
        this.isLoading = true;
        const userId = this.user.userID;
        this.userApiService.deactivateUser(userId, this.userStatusPayload()).subscribe({
            next: (response) => {
                this.isLoading = false;
                if (response.status === 'Success') {
                    this.userAdminService.showToastBar('success', `User ${userId} has been deactivated successfully.`, 'deactivateUser');
                    this.userApiService.getUserDetailsById(this.route.snapshot.params.id).subscribe((data) => {
                        this.user = data[0];
                    });
                }
            },
            error: (error) => {
                if (error) {
                    this.isLoading = false;
                    this.userAdminService.showToastBar('error', this.translate.instant('ACCOUNT_ADMIN.DEACTIVATE_USER_ERROR'), 'deactivateUser');
                }
            }
        });
    }

    backToSearchResults() {
        const navigateToPath = this.accountAdminService.isUserFromContactsSection ?
            '/Admin/Admin-Tools/account-admin.html/results/contacts' :
            '/Admin/Admin-Tools/account-admin.html/results/users';
        this.router.navigate([navigateToPath],
            {
                relativeTo: this.route,
                queryParams: this.accountAdminService.lastQueryParams.value,
                queryParamsHandling: 'merge'
            });
    }

    reactivateUser() {
        this.activateUserModal = false;
        this.isLoading = true;
        const userId = this.user.userID;
        this.userApiService.reactivateUser(userId, this.userStatusPayload()).subscribe({
            next: (response) => {
                this.isLoading = false;
                if (response.status === 'Success') {
                    this.userAdminService.showToastBar('success', `User ${userId} has been reactivated successfully.`, 'reactivateUser');
                    this.userApiService.getUserDetailsById(this.route.snapshot.params.id).subscribe((data) => {
                        this.user = data[0];
                    });
                }
            },
            error: (error) => {
                if (error) {
                    this.isLoading = false;
                    this.userAdminService.showToastBar('error', this.translate.instant('ACCOUNT_ADMIN.REACTIVATE_USER_ERROR'), 'reactivateUser');
                }
            }
        });
    }

    CreateUserDetails(event: UserDetails) {
        this.userDetails = event;
    }

    onUserCompanyChangeClick() {
        this.isLoading = true;
        this.accountAdminApiService.getAccountAdminCompanyDetails(this.user.companyId).subscribe((company: Company) => {
            this.userCompanyTypeCode = this.userAdminService.removePrefixLetterZ(company.companyType?.code || '');
            this.userCompanyTypeName = company.companyType?.name || '';
            this.userCompanyBrandFamily = company.brandFamily;
            this.isLoading = false;
        });

        this._userCompanyEditMode = true;
    }

    onCompanySearchClick() {
        this._isCompanyListDataAvailable = true;
    }

    isCheckboxDisabled(): boolean {
        return !this.initialContactOnly;
    }


    public onCompanySelect(company: CompanyResultDataResponse | null): void {
        if (company) {
            this.userAdminService.userEditForm.controls.employerName.setValue(company.companyName);
            this.userAdminService.userEditForm.controls.companyId.setValue(company.hvacCompanyId);
            this.userAdminService.userEditForm.controls.employerName.markAsDirty();
            this.userAdminService.userEditForm.controls.companyId.markAsDirty();
        }
        else {
            this.userAdminService.showToastBar('error', this.translate.instant('USER_ADMIN.USER_EDIT.ERROR.SOMETHING_WENT_WRONG'));
        }
        this._userCompanyEditMode = false;
    }

    public onClickClearSearch() {
        this.accountAdminService.advancedSearchFormForChangeCompany.controls.companyId.reset(this.accountAdminService.advancedSearchFormDefault.value.companyId);
        this.accountAdminService.advancedSearchFormForChangeCompany.controls.companyName.reset(this.accountAdminService.advancedSearchFormDefault.value.companyName);
        this.accountAdminService.advancedSearchFormForChangeCompany.controls.soldTo.reset(this.accountAdminService.advancedSearchFormDefault.value.soldTo);
        this.accountAdminService.advancedSearchFormForChangeCompany.controls.address1.reset(this.accountAdminService.advancedSearchFormDefault.value.address1);
        this.accountAdminService.advancedSearchFormForChangeCompany.controls.city.reset(this.accountAdminService.advancedSearchFormDefault.value.city);
        this.accountAdminService.advancedSearchFormForChangeCompany.controls.country.reset(this.accountAdminService.advancedSearchFormDefault.value.country);
        this.accountAdminService.advancedSearchFormForChangeCompany.controls.region.reset(this.accountAdminService.advancedSearchFormDefault.value.region);
        this.accountAdminService.advancedSearchFormForChangeCompany.controls.postalCode.reset(this.accountAdminService.advancedSearchFormDefault.value.postalCode);
    }

    public searchCompanyModalClose() {
        this._userCompanyEditMode = false;
    }

    public onExportUserDetails(exportUser: UserDetailsEntity) {
        const selectedUser = this.userAdminService.transformToExportUserDetailFormart(exportUser, this.userExportDetails);
        this.ExportService.doExportUserXSLX(selectedUser);
    }

    public getFormattedPhone(phone: string): string {
        if (!phone) {
            return '';
        }

        return this.commonUtils.formatPhoneNumber(phone, this.user.userCountry);
    }

    public formatPhoneNumber(event: Event, controlName: string) {
        const countryCode = this.getSelectedCountryCode();
        const target = event.target as HTMLInputElement;
        if (target) {
            const formattedValue = this.commonUtils.formatPhoneNumber(target.value, countryCode);
            this.userAdminService.userEditForm.controls[controlName].setValue(formattedValue);
        }
    }

    public getPhonePlaceholder(fieldType: string): string {
        const countryCode = this.getSelectedCountryCode();

        return this.commonUtils.getPhonePlaceholder(fieldType, countryCode);
    }

    private userStatusPayload(): UserDetailsEntity {
        return { ...this.user };
    }

    private setUserRelationshipEligibility(companyId: string): void {
        if (!companyId) {
            this.canManageUserRelationship = false;
            this.isLoading = false;

            return;
        }

        this.accountAdminExtendedService.getAccountAdminCompanyById(companyId).subscribe((company) => {
            this.canManageUserRelationship = relationshipEligibleCategories.includes(company?.companyCategory?.name || '');
            this.isLoading = false;
        }, () => {
            this.canManageUserRelationship = false;
            this.isLoading = false;
        });
    }

    private checkIfUserIsHybrisEligible(): boolean {
        const hasHybrisFeatureInAdditional = this.user?.additionalFeatures?.some((element) => hybrisEligibleFeatureNamesList.includes(element.featureName)) ?? false;
        const hasHybrisFeatureInRoles = this.user?.userRolesDetails?.some((role) => role.userFeatures?.some((feature) => hybrisEligibleFeatureNamesList.includes(feature.featureName))) ?? false;

        return hasHybrisFeatureInAdditional || hasHybrisFeatureInRoles;
    }

    private getSelectedCountryCode(): string {
        const selectedCountry = this.userAdminService.userEditForm.controls.userCountry.value;
        if (Array.isArray(selectedCountry)) {
            return selectedCountry[0]?.value || '';
        }
        const countrySelection = this.countryOptionData$.value.find((country) => country.name === selectedCountry);

        return countrySelection?.value || '';
    }

    private getSelectedStateCode(): string {
        const selectedState = this.userAdminService.userEditForm.controls.userState.value;
        if (Array.isArray(selectedState)) {
            return selectedState[0]?.value || '';
        }
        const stateSelection = this.stateOptionData$.value.find((state) => state.name === selectedState);

        return stateSelection?.value || '';
    }

    private getStateListByCountry(selectedCountry: string) {
        this.commonUtils.getStateByCountry(selectedCountry).subscribe((stateList) => {
            this.stateOptionData$.next(stateList);
            this.userState = this.stateOptionData$.value.find((state) => state.value === this.user.userState)?.name || '';
        });
    }

    private showPendingUserCreationSuccessToast(): void {
        if (!this.pendingUserCreationSuccessMessage) {
            return;
        }
        this.userAdminService.smoothScrollToTop();
        this.toastService.add({
            content: this.pendingUserCreationSuccessMessage,
            theme: 'success',
            id: 'createUser',
            closeable: true,
            autoClose: true,
            timeOut: AppConstants.timeOutNavigation5Sec
        });
        delete this.pendingUserCreationSuccessMessage;
    }
}
